import { useCallback, useLayoutEffect, useMemo, useReducer } from 'react';
import {
  defaultState,
  genericFormActions,
  getValidityState,
  makeReducer
} from '../forms/genericForm/state';
import { makeRequiredValidator } from '../utils/makeRequiredValidator';
import { validate } from '@cube3/main/src/components/app/layout/Exports/state/useGenericExportOptions';
import {
  Field,
  FieldWithState,
  Validator,
  Value
} from '../types/GenericFormTypes';
import { getFieldErrors } from '../utils/getFieldErrors';
import { getFieldValue } from '../utils/getFieldValue';
import { makeGenericFormFields } from '../utils/makeGenericFormFields';

export interface GenericFormProps {
  fields: Field[];
  initialValues?: Value[];
  validators?: Validator[];
  onSubmit?: (val?) => void;
  /** automatically make validators for fields with `required: true` */
  makeRequiredValidators?: boolean;
  /** add some legacy properties to the field props so old forms that used `useGenericExportOptions` keep working */
  compatibilityMode?: boolean;
}

const emptyArray = [];

export const useGenericFormState = ({
  fields: initialFields = emptyArray,
  onSubmit,
  /** Validators for all the fields */
  validators = emptyArray,
  initialValues = emptyArray,
  makeRequiredValidators = true,
  compatibilityMode
}: GenericFormProps) => {
  // run validators at initial run

  // Add default parser, formatter and validator to each field
  const fields = useMemo(() => {
    return makeGenericFormFields(initialFields);
  }, [initialFields]);

  const extendedValidators = useMemo(
    () =>
      makeRequiredValidators
        ? [
            ...(validators || []),
            ...fields
              .map((f) =>
                f.required ? makeRequiredValidator(f.id) : undefined
              )
              .filter(Boolean)
          ]
        : validators,
    [validators, fields]
  );

  const initialState = useMemo(() => {
    // optionally generate "required" validators

    // run validators at initial run
    const initialErrors = extendedValidators
      ? validate(extendedValidators, initialValues, fields)
      : undefined;

    return {
      ...defaultState,
      fields: fields || defaultState.fields,
      values: initialValues || defaultState.values,
      valid: getValidityState(initialErrors.errors, initialErrors.warnings),
      errors: initialErrors?.errors
        ? initialErrors?.errors
        : defaultState.errors,
      warnings: initialErrors?.warnings
        ? initialErrors?.warnings
        : defaultState.warnings,
      touched: []
    };
  }, [makeRequiredValidators, validators, fields, initialValues]);

  const reducer = useCallback(makeReducer(extendedValidators), [
    extendedValidators
  ]);

  const [state, dispatch] = useReducer(reducer, initialState);

  /** Field change handler */
  const handleChange = useCallback(
    (field: Field) => (e) => {
      const id = field.id;
      const value = e?.target?.value || e;
      let parsed = value;
      if (typeof field.parse === 'function') {
        parsed = field.parse(value);
      }
      dispatch({
        type: genericFormActions.CHANGE_VALUE,
        payload: { id, value: parsed }
      });
    },
    [state.touched, dispatch]
  );

  /** Field blur handler */
  const handleBlur = useCallback(
    (field: Field) => (e) => {
      const id = field.id;
      if (!state.touched.includes(id)) {
        dispatch({
          type: genericFormActions.CHANGE_VALUE,
          payload: { id: id, value: state.values[id] }
        });
      }
    },
    [state.values, state.touched]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (state.valid && onSubmit) {
        onSubmit(state.values);
      }
    },
    [state.valid, state.values, onSubmit]
  );

  const initializeForm = useCallback(() => {
    dispatch({
      type: genericFormActions.INITIALIZE,
      payload: initialState
    });
  }, [dispatch, initialState]);

  useLayoutEffect(() => {
    initializeForm();
  }, [initialValues]);

  const extendedFields = useMemo(() => {
    return fields
      .map((field) => {
        const id = field.id;

        /** Use case: a field's visibility is depends on other field,
         * - e.g.: `Select download type` options is only visible when `allow to download` checkbox is checked
         */
        const isVisible =
          typeof field.visible === 'function'
            ? field.visible(state.values)
            : field.visible ?? true;

        if (!isVisible) {
          return;
        }

        const errors = getFieldErrors(state.errors, id)?.length
          ? getFieldErrors(state.errors, id)
          : undefined;

        const value = getFieldValue(state.values, id)?.value;

        return {
          ...field,
          visible: true,
          name: field.id,
          // meta:
          touched: state.touched.includes(id),
          invalid: !!errors?.length,
          valid: !errors?.length,
          error: errors,
          warning: getFieldErrors(state.warnings, id),

          // input
          value:
            typeof field.format === 'function' ? field.format(value) : value,
          onChange: handleChange(field),
          onBlur: handleBlur(field)
        } as FieldWithState;
      })
      .filter(Boolean);
  }, [fields, state, compatibilityMode]);

  return {
    ...state,
    fields: extendedFields,
    dirty: !!state.touched.length,
    handleSubmit,
    initializeForm
  };
};
